import React from "react";
import { Helmet } from "react-helmet-async";
import TopNavbar from "@/components/layout/TopNavbar";
import Footer from "@/components/home/Footer";
import { Button } from "@/components/ui/button";
import { Music, Star, Calendar, Heart } from "lucide-react";
import { Link } from 'react-router-dom';

const HERO_IMAGE_URL = "https://media.hivetivity.com/dad-teaching-girl-play-guitar.jpg";

const MusicSchools = () => {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Music School Management Software - Hivetivity</title>
        <meta name="description" content="Streamline your music school operations with Hivetivity. Manage lessons, track student progress, and handle instrument rentals efficiently." />
      </Helmet>

      <TopNavbar />

      <main>
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-b from-green-50 to-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-[#5a5a59] mb-6">
                  Orchestrate Your Music School Success
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Focus on inspiring young musicians while we handle the administrative harmony with our comprehensive management platform.
                </p>
                <Link to="/business-trial">
                  <Button className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg">
                    Start Free Trial
                  </Button>
                </Link>
              </div>
              <div className="relative">
                <img
                  src={HERO_IMAGE_URL}
                  alt="Dad teaching girl to play guitar"
                  width={600}
                  height={400}
                  className="rounded-2xl shadow-xl"
                  style={{ objectFit: 'cover', width: '100%', height: 'auto' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-[#5a5a59] mb-12">
              Everything Your Music School Needs
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="p-6 rounded-xl bg-green-50 hover:bg-green-100 transition-colors">
                <Music className="w-12 h-12 text-green-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Lesson Management</h3>
                <p className="text-gray-600">Schedule and track private and group lessons.</p>
              </div>
              <div className="p-6 rounded-xl bg-green-50 hover:bg-green-100 transition-colors">
                <Star className="w-12 h-12 text-green-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Progress Tracking</h3>
                <p className="text-gray-600">Monitor student development and achievements.</p>
              </div>
              <div className="p-6 rounded-xl bg-green-50 hover:bg-green-100 transition-colors">
                <Calendar className="w-12 h-12 text-green-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Recital Planning</h3>
                <p className="text-gray-600">Organize performances and student showcases.</p>
              </div>
              <div className="p-6 rounded-xl bg-green-50 hover:bg-green-100 transition-colors">
                <Heart className="w-12 h-12 text-green-500 mb-4" />
                <h3 className="text-xl font-semibold text-[#5a5a59] mb-2">Instrument Rentals</h3>
                <p className="text-gray-600">Manage instrument inventory and maintenance.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonial Section */}
        <section className="py-20 bg-green-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold text-[#5a5a59] mb-8">
                Loved by Music Educators
              </h2>
              <blockquote className="text-xl text-gray-600 italic mb-6">
                "Hivetivity has brought harmony to our music school operations. The lesson scheduling and progress tracking features are amazing."
              </blockquote>
              <p className="font-semibold text-[#5a5a59]">
                - David Chang, Music Academy Director
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-[#5a5a59] mb-6">
              Ready to Transform Your Music School?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join hundreds of music schools using Hivetivity to create inspiring educational experiences.
            </p>
            <Link to="/business-trial">
              <Button className="bg-green-500 hover:bg-green-600 text-white px-8 py-6 text-lg">
                Start Your Free Trial Today
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default MusicSchools;
